import { useEffect, useState } from 'react';
import { IconCheck, IconAlertCircle, IconReceipt, IconCash, IconPercentage, IconBuildingStore } from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useSettings, type Settings } from '../../hooks/useSettings';

/**
 * Phase 4.4 — Settings page. ADMIN+ surface for everything that's a
 * toggle / threshold / cap, plus the business-profile fields that
 * print on receipts.
 */

export default function AdminSettingsPage() {
  const { settings, loading, refresh } = useSettings();
  const [draft, setDraft] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => { setDraft(settings); }, [settings]);

  if (!draft) {
    return <AdminLayout title="Settings"><p className="text-13 italic text-gray-700">Loading…</p></AdminLayout>;
  }

  function set<K extends keyof Settings>(key: K, val: Settings[K]) {
    setDraft((d) => d ? { ...d, [key]: val } : d);
  }

  function dirty(key: keyof Settings) {
    return draft && settings[key] !== draft[key];
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const body: Partial<Settings> = {};
      for (const k of Object.keys(draft) as (keyof Settings)[]) {
        if (dirty(k)) {
          (body as Record<string, unknown>)[k] = draft[k];
        }
      }
      const r = await fetch('/api/settings', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      await refresh();
      setFlash('Saved');
      setTimeout(() => setFlash(null), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const hasChanges = (Object.keys(draft) as (keyof Settings)[]).some(dirty);

  return (
    <AdminLayout
      title="Settings"
      subtitle="Branch-level toggles, thresholds, and profile"
      actions={
        <button
          type="button"
          onClick={save}
          disabled={saving || !hasChanges}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <IconCheck size={16} stroke={2.5} />
          {saving ? 'Saving…' : (flash ?? 'Save changes')}
        </button>
      }
    >
      {loading && <p className="text-13 italic text-gray-700 mb-3">Loading…</p>}
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <div className="space-y-5">
        {/* Operations */}
        <Card title="Operations" Icon={IconReceipt}>
          <Toggle
            label="Tips enabled at till"
            helper="When OFF, the tip picker is hidden and tipCents always 0."
            checked={draft.tipsEnabled}
            onChange={(v) => set('tipsEnabled', v)}
          />
          <Toggle
            label="Split bill enabled"
            helper="When OFF, the single-payment flow is the only option."
            checked={draft.splitBillEnabled}
            onChange={(v) => set('splitBillEnabled', v)}
          />
          <CentsInput
            label="Variance threshold"
            helper="Cash count vs expected — over this, day-close needs manager PIN."
            value={draft.varianceThresholdCents}
            onChange={(v) => set('varianceThresholdCents', v)}
          />
        </Card>

        {/* Discount caps */}
        <Card title="Discount caps" Icon={IconPercentage}>
          <BpsInput
            label="Cashier cap"
            helper="% off subtotal that a cashier can apply without manager PIN."
            value={draft.discountCapBpsCashier}
            onChange={(v) => set('discountCapBpsCashier', v)}
          />
          <BpsInput
            label="Supervisor cap"
            helper="% off subtotal that a supervisor can apply without manager PIN."
            value={draft.discountCapBpsSupervisor}
            onChange={(v) => set('discountCapBpsSupervisor', v)}
          />
          <p className="text-12 font-medium text-gray-700">
            Manager + Admin + Owner are unlimited by design.
          </p>
        </Card>

        {/* House accounts */}
        <Card title="House accounts" Icon={IconCash}>
          <CentsInput
            label="Default credit limit"
            helper="Suggested limit when an admin opens a new account. Per-account limits override this."
            value={draft.defaultAccountCreditLimitCents}
            onChange={(v) => set('defaultAccountCreditLimitCents', v)}
          />
        </Card>

        {/* Business profile */}
        <Card title="Business profile" Icon={IconBuildingStore}>
          <p className="text-12 font-medium text-gray-700 mb-3">
            These fields print on every receipt and tax invoice.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextInput label="Legal name" value={draft.legalName ?? ''} onChange={(v) => set('legalName', v || null)} placeholder="Habby & Lace (Pty) Ltd" />
            <TextInput label="Trading name" value={draft.tradingName ?? ''} onChange={(v) => set('tradingName', v || null)} placeholder="HL Coffee Shop" />
            <TextInput label="Address line 1" value={draft.addressLine1 ?? ''} onChange={(v) => set('addressLine1', v || null)} />
            <TextInput label="Address line 2" value={draft.addressLine2 ?? ''} onChange={(v) => set('addressLine2', v || null)} />
            <TextInput label="City" value={draft.city ?? ''} onChange={(v) => set('city', v || null)} />
            <TextInput label="Postal code" value={draft.postalCode ?? ''} onChange={(v) => set('postalCode', v || null)} />
            <TextInput label="Country" value={draft.country} onChange={(v) => set('country', v)} />
            <TextInput label="Phone" value={draft.phone ?? ''} onChange={(v) => set('phone', v || null)} />
            <TextInput label="Email" value={draft.email ?? ''} onChange={(v) => set('email', v || null)} type="email" />
            <TextInput label="VAT number" value={draft.vatNumber ?? ''} onChange={(v) => set('vatNumber', v || null)} placeholder="optional — pending shows on receipt if blank" />
            <TextInput label="Invoice prefix" value={draft.invoiceNumberPrefix ?? ''} onChange={(v) => set('invoiceNumberPrefix', v || null)} placeholder="e.g. INV-" />
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

function Card({ title, Icon, children }: { title: string; Icon: typeof IconReceipt; children: React.ReactNode }) {
  return (
    <section className="rounded-lg bg-surface border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-primary" />
        <h2 className="text-15 font-bold text-heading">{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Toggle({ label, helper, checked, onChange }: { label: string; helper?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-md bg-light border border-border-soft cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
      <span className="flex-1">
        <span className="text-14 font-bold text-heading block">{label}</span>
        {helper && <span className="text-12 font-medium text-gray-700 block">{helper}</span>}
      </span>
    </label>
  );
}

function CentsInput({ label, helper, value, onChange }: { label: string; helper?: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
        {label}
        {helper && <span className="ml-2 text-gray-600 normal-case tracking-normal font-medium">— {helper}</span>}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-13 font-bold text-gray-700">R</span>
        <input
          type="text"
          inputMode="decimal"
          value={(value / 100).toFixed(2)}
          onChange={(e) => {
            const v = Number(e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'));
            if (isFinite(v) && v >= 0) onChange(Math.round(v * 100));
          }}
          className="w-44 h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-bold tabular-nums text-heading"
        />
      </div>
    </label>
  );
}

function BpsInput({ label, helper, value, onChange }: { label: string; helper?: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
        {label}
        {helper && <span className="ml-2 text-gray-600 normal-case tracking-normal font-medium">— {helper}</span>}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          value={(value / 100).toFixed(0)}
          onChange={(e) => {
            const v = Number(e.target.value.replace(/[^0-9.]/g, ''));
            if (isFinite(v) && v >= 0 && v <= 100) onChange(Math.round(v * 100));
          }}
          className="w-24 h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-bold tabular-nums text-heading"
        />
        <span className="text-13 font-bold text-gray-700">%</span>
      </div>
    </label>
  );
}

function TextInput({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
      />
    </label>
  );
}
